// Right-side thread drawer. Fixed Header, scroll-area Body, compact Footer.

import { renderMarkdown } from '../../functions/chat/renderMarkdown.js'
import { setupHoverActions } from '../../functions/chat/setupHoverActions.js'

const _parentMessage = (root) => {
  if (!root || !root.activeChannelId || !root.activeThreadRootId) return null
  const list = (root.messagesByChannel || {})[root.activeChannelId] || []
  return list.find((m) => m.id === root.activeThreadRootId) || null
}

const _isMine = (s) =>
  !!(s && s.root && s.authorEmail && s.authorEmail === s.root.currentUserEmail)

export const ChatThreadPanel = {
  flow: 'y',
  width: 'I',
  minWidth: 'H',
  maxWidth: 'I1',
  height: '100%',
  minHeight: '0',
  background: 'document',

  Header: {
    flow: 'x',
    align: 'center space-between',
    padding: 'A B',
    borderBottomWidth: '1px',
    borderBottomColor: 'line',
    borderBottomStyle: 'solid',
    flex: '0 0 auto',

    TitleGroup: {
      flow: 'y',
      gap: 'V',
      Title: {
        tag: 'h3',
        margin: '0',
        fontSize: 'A',
        fontWeight: '700',
        color: 'title',
        text: 'Thread'
      },
      Subtitle: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) => {
          const root = s && s.root
          if (!root) return ''
          const ch = (root.channels || []).find(
            (c) => c.id === root.activeChannelId
          )
          if (!ch) return ''
          if (ch.kind === 'channel') return '# ' + (ch.name || '')
          if (ch.kind === 'dm') return 'Direct message'
          // group_dm — show first names of the other participants
          return el.call('channelDisplayName', ch, root.channelMembers || [],
            el.call('peopleByEmail'), root.currentUserEmail, { firstOnly: true })
        }
      }
    },
    CloseBtn: {
      extends: 'IconButton',
      Icon: { icon: 'dismiss' },
      attr: { 'data-action': 'close-thread' }
    }
  },

  Body: {
    flex: '1 1 0',
    minHeight: '0',
    flow: 'y',
    overflow: 'auto',
    overscrollBehavior: 'contain',
    padding: 'A',
    gap: 'A',

    onCreate: (el) => {
      if (!el || !el.node) return
      // Reuse the shared overlay for thread replies — same positioning
      // semantics as the main message list.
      setupHoverActions(el.node)
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (el && el.node) el.node.scrollTop = el.node.scrollHeight
        })
      )
    },
    onUpdate: (el, s) => {
      if (!el || !el.node) return
      const root = s && s.root
      const ch = root && root.activeChannelId
      const tid = root && root.activeThreadRootId
      const list =
        (root && root.messagesByChannel && root.messagesByChannel[ch]) || []
      const replyCount = list.filter((m) => m.threadRoot === tid).length
      const sig = `${tid}:${replyCount}`
      if (el._lastSig === sig) return
      el._lastSig = sig
      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          if (el && el.node) el.node.scrollTop = el.node.scrollHeight
        })
      )
    },

    // Parent message — the root of the thread.
    Parent: {
      flow: 'x',
      gap: 'Z1',
      paddingBottom: 'A',
      borderBottomWidth: '1px',
      borderBottomColor: 'line',
      borderBottomStyle: 'solid',

      Avatar: {
        avatarType: 'initials',
        boxSize: 'B1 B1',
        round: '50%',
        flex: '0 0 auto',
        cursor: 'pointer',
        attr: {
          'data-personrow': 'true',
          'data-email': (el, s) => {
            const parent = _parentMessage(s && s.root)
            return (parent && parent.authorEmail) || ''
          }
        },
        src: (el, s) => {
          const parent = _parentMessage(s && s.root)
          const person = parent && el.call('findPerson', parent.authorEmail)
          return el.call(
            'avatarUrl',
            (person && person.name) || (parent && parent.authorEmail)
          )
        }
      },

      Content: {
        flex: '1 1 0',
        flow: 'y',
        gap: '0',
        minWidth: '0',

        Meta: {
          flow: 'x',
          gap: 'Z',
          align: 'baseline left',
          Author: {
            tag: 'span',
            fontSize: 'Z2',
            fontWeight: '700',
            color: 'title',
            text: (el, s) => {
              const parent = _parentMessage(s && s.root)
              const person = parent && el.call('findPerson', parent.authorEmail)
              return (
                (person && person.name) || (parent && parent.authorEmail) || ''
              )
            }
          },
          Time: {
            tag: 'span',
            fontSize: 'Y2',
            color: 'caption',
            text: (el, s) => {
              const parent = _parentMessage(s && s.root)
              return parent && parent.createdAt
                ? el.call('clockTime', parent.createdAt)
                : ''
            }
          }
        },
        Body: {
          tag: 'div',
          fontSize: 'Z2',
          lineHeight: '1.5',
          color: 'title',
          wordBreak: 'break-word',
          padding: (el, s) => {
            const p = _parentMessage(s && s.root)
            return p && p.body ? 'Z1 A' : '0'
          },
          round: 'A A A V',
          background: (el, s) => {
            const p = _parentMessage(s && s.root)
            return p && p.body ? 'white.06' : 'transparent'
          },
          borderWidth: (el, s) => {
            const p = _parentMessage(s && s.root)
            return p && p.body ? '1px' : '0'
          },
          borderStyle: 'solid',
          borderColor: 'white.08',
          marginTop: (el, s) => {
            const p = _parentMessage(s && s.root)
            return p && p.body ? 'Z' : '0'
          },
          maxWidth: '100%',
          // Use reactive `text` so the body always renders even when
          // el.set() from renderMarkdown misses due to timing. The
          // onUpdate will upgrade it to formatted markdown when it fires.
          text: (el, s) => {
            const p = _parentMessage(s && s.root)
            return (p && p.body) || ''
          },
          onUpdate: (el, s) => {
            const p = _parentMessage(s && s.root)
            if (p && p.body) renderMarkdown(el, p.body, null, s.root.people)
          }
        }
      }
    },

    ReplyCount: {
      tag: 'span',
      fontSize: 'Y2',
      fontWeight: '600',
      color: 'caption',
      letterSpacing: '.04em',
      padding: 'V 0',
      text: (el, s) => {
        const root = s && s.root
        if (!root || !root.activeChannelId) return ''
        const list = (root.messagesByChannel || {})[root.activeChannelId] || []
        const n = list.filter(
          (m) => m.threadRoot === root.activeThreadRootId
        ).length
        return n === 0 ? 'No replies yet' : `${n} repl${n === 1 ? 'y' : 'ies'}`
      }
    },

    Replies: {
      flow: 'y',
      gap: 'Z',
      childExtends: 'ChatThreadReplyRow',
      childrenAs: 'state',
      children: (el, s) => {
        const root = s && s.root
        if (!root || !root.activeChannelId || !root.activeThreadRootId)
          return []
        const map = root.messagesByChannel || {}
        const list = map[root.activeChannelId] || []
        return list.filter((m) => m.threadRoot === root.activeThreadRootId)
      }
    }
  },

  Footer: {
    flow: 'y',
    position: 'relative',
    padding: 'Z A',
    background: 'transparent',
    flex: '0 0 auto',

    // Send icon pinned top-right, matching the main composer pattern.
    SendBtn: {
      extends: 'IconButton',
      position: 'absolute',
      top: 'Z',
      right: 'Z',
      zIndex: '1',
      Icon: { icon: 'arrowAngleRight' },
      '@dark': {
        background: 'white',
        color: 'title',
        ':hover': { background: 'gray14', color: 'gray1' },
        ':active': { background: 'gray13', color: 'gray1' }
      },
      onClick: (e, el, s) => {
        e.preventDefault()
        const root = s && s.root
        if (!root || !root.activeChannelId || !root.activeThreadRootId) return
        const ta = el.parent && el.parent.Field && el.parent.Field.node
        const body = ((ta && ta.value) || '').trim()
        if (!body) return
        el.call('sendMessage', {
          channelId: root.activeChannelId,
          body,
          replyTo: root.activeThreadRootId,
          threadRoot: root.activeThreadRootId
        })
        if (ta) {
          ta.value = ''
          ta.style.height = 'auto'
        }
      }
    },

    Field: {
      tag: 'textarea',
      width: '100%',
      minHeight: 'B1',
      maxHeight: 'E',
      padding: 'Z A',
      round: 'A',
      theme: 'field',
      color: 'title',
      fontSize: 'Z2',
      lineHeight: '1.5',
      resize: 'none',
      outline: 'none',
      attr: {
        placeholder: 'Reply to thread…',
        rows: 2
      },
      onInput: (e, el) => {
        if (el && el.node) {
          el.node.style.height = 'auto'
          el.node.style.height = Math.min(el.node.scrollHeight, 200) + 'px'
        }
      },
      onKeydown: (e, el, s) => {
        if (e.key !== 'Enter' || e.shiftKey) return
        e.preventDefault()
        const root = s && s.root
        if (!root || !root.activeChannelId || !root.activeThreadRootId) return
        const body = (e.target.value || '').trim()
        if (!body) return
        el.call('sendMessage', {
          channelId: root.activeChannelId,
          body,
          replyTo: root.activeThreadRootId,
          threadRoot: root.activeThreadRootId
        })
        e.target.value = ''
        if (el.node) el.node.style.height = 'auto'
      }
    }
  }
}

export const ChatThreadReplyRow = {
  flow: 'x',
  gap: 'Z1',
  align: 'flex-start left',
  padding: 'Z 0',

  Avatar: {
    avatarType: 'initials',
    boxSize: 'B1 B1',
    round: '50%',
    flex: '0 0 auto',
    cursor: 'pointer',
    attr: {
      'data-personrow': 'true',
      'data-email': (el, s) => (s && s.authorEmail) || ''
    },
    src: (el, s) => {
      if (!s) return el.call('avatarUrl', null)
      const person = el.call('findPerson', s.authorEmail)
      return el.call('avatarUrl', (person && person.name) || s.authorEmail)
    }
  },

  Content: {
    flex: '1 1 0',
    flow: 'y',
    gap: '0',
    minWidth: '0',

    Meta: {
      flow: 'x',
      gap: 'Z',
      align: 'baseline left',

      Author: {
        tag: 'span',
        fontSize: 'Z2',
        fontWeight: '700',
        color: 'title',
        text: (el, s) => {
          if (!s) return ''
          const person = el.call('findPerson', s.authorEmail)
          return (person && person.name) || s.authorEmail || ''
        }
      },
      Time: {
        tag: 'span',
        fontSize: 'Y2',
        color: 'caption',
        text: (el, s) =>
          s && s.createdAt ? el.call('clockTime', s.createdAt) : ''
      }
    },

    Body: {
      hide: (el, s) => !(s && s.body),
      tag: 'div',
      fontSize: 'Z2',
      lineHeight: '1.5',
      color: 'title',
      wordBreak: 'break-word',
      padding: 'Z1 A',
      round: (el, s) => (_isMine(s) ? 'A A V A' : 'A A A V'),
      background: (el, s) => (_isMine(s) ? 'blue.08' : 'white.06'),
      borderWidth: '1px',
      borderStyle: 'solid',
      borderColor: (el, s) => (_isMine(s) ? 'blue.15' : 'white.08'),
      marginTop: 'V',
      maxWidth: '100%',
      text: (el, s) => (s && s.body) || '',
      onUpdate: (el, s) => {
        if (s && s.body)
          renderMarkdown(el, s.body, null, s && s.root && s.root.people)
      }
    }
  }
}
